import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { CategoryGrid } from '../components/CategoryGrid';
import { FeaturedSections } from '../components/FeaturedSections';
import { HomepageCards } from '../components/HomepageCards';
import { seedData } from '../lib/api';

const heroImages = [
  'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=1920',
  'https://images.unsplash.com/photo-1528164344705-47542687000d?w=1920',
  'https://images.unsplash.com/photo-1548574505-5e239809ee19?w=1920',
  'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1920'
];

export default function HomePage() {
  const [currentImage, setCurrentImage] = useState(0);
  const [seeded, setSeeded] = useState(false);
  const navigate = useNavigate();

  // Seed data on mount
  useEffect(() => {
    const initSeed = async () => {
      try {
        await seedData();
        setSeeded(true);
      } catch {
        // Data might already be seeded - this is expected
        setSeeded(true);
      }
    };
    initSeed();
  }, []);

  // Rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const openAIChat = () => {
    navigate('/ai-chat');
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] overflow-hidden" data-testid="hero-section">
        {/* Background Images */}
        {heroImages.map((img, index) => (
          <div
            key={img}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              currentImage === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img}
              alt="Travel destination"
              className="w-full h-full object-cover"
            />
          </div>
        ))}

        {/* Overlay */}
        <div className="hero-overlay absolute inset-0" />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 pt-20">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8 animate-fade-in">
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-sm text-white/80">AI Travel Concierge</span>
            </div>

            {/* Main Title */}
            <h1 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white mb-6 animate-slide-up tracking-tight">
              Găsește oferta perfectă
              <br />
              <span className="text-gradient-gold">în câteva secunde</span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-white/70 mb-10 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '200ms' }}>
              Mai puțin timp căutând, mai mult timp planificând călătoria perfectă!
            </p>

            {/* CTA Button */}
            <Button
              onClick={openAIChat}
              className="btn-gold px-8 py-4 text-lg rounded-full animate-fade-in gold-glow"
              style={{ animationDelay: '400ms' }}
              data-testid="hero-cta-btn"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Inițiază o conversație cu AI
            </Button>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-white/50" />
          </div>
        </div>

        {/* Image indicators */}
        <div className="absolute bottom-8 right-8 z-10 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImage(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                currentImage === index 
                  ? 'bg-gold w-6' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              data-testid={`hero-indicator-${index}`}
            />
          ))}
        </div>
      </section>

      {/* Categories Section */}
      <CategoryGrid />

      {/* Homepage Cards Section (from admin) */}
      <HomepageCards />

      {/* Featured Sections */}
      <FeaturedSections />
    </div>
  );
}
